import type {
	EmissionValueAnsweredUnit,
	EmissionValues,
	ProductOrServiceCategory,
} from '@api-client/api'
import { Section } from '@components/Section'
import {
	calculateCategoryTotal,
	calculateSubcategoryTotals,
	calculateTotalEmissions,
	isCalculator,
	isDetailedLifecycle,
	isLifecycle,
} from '@helpers/emissions'
import { formatEmissions } from '@helpers/numbers'
import { getIsTransportationService } from '@helpers/productOrService'
import { useAppSelector } from '@hooks/reduxhooks'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { getProductInfo } from '@store/emissionsDataForm/emissionDataFormSlice'
import { useEmissionFormParams } from '@views/EmissionsDataForm/useProductRequestData'
import { HighlightedContainer } from '@views/supplier/Calculator/Form/styles'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Container, StickyStack } from './styles'

type Props = { viewType: 'customer' | 'supplier' } & Pick<
	Partial<EmissionValues>,
	'emissionValuesGranularity' | 'emissionsValuesBreakdown'
> & {
		unit?: EmissionValueAnsweredUnit
		productOrServiceCategory?: ProductOrServiceCategory
	}

// TODO(pascal): Address accessibility of use of h1 components in this file
const TotalSection: React.FC<Props> = ({
	viewType,
	emissionValuesGranularity,
	emissionsValuesBreakdown,
	unit,
	productOrServiceCategory,
}) => {
	const theme = useTheme()
	const { isTemplate } = useEmissionFormParams()
	const { t } = useTranslation()

	// Left out of useMemo due to complexity of constructing the dependency array
	const totalEmissions = calculateTotalEmissions(
		emissionValuesGranularity,
		emissionsValuesBreakdown
	)

	const areCategoriesShown = useMemo(
		() =>
			isLifecycle(emissionValuesGranularity) ||
			isDetailedLifecycle(emissionValuesGranularity),
		[emissionValuesGranularity]
	)

	const areSubcategoriesShown = useMemo(
		() =>
			isDetailedLifecycle(emissionValuesGranularity) ||
			isCalculator(emissionValuesGranularity),
		[emissionValuesGranularity]
	)

	const categories = [
		'materialAcquisitionAndPreprocessing',
		'production',
		'distributionAndStorage',
	] as const

	const getFormattedTotalWithUnit = useCallback(
		(value: number) => formatEmissions(value, unit, t),
		[unit, t]
	)
	const productInfo = useAppSelector(state => getProductInfo(state, isTemplate))

	const translationContext = getIsTransportationService(
		productOrServiceCategory,
		productInfo.unit
	)
		? 'transportation'
		: undefined

	return (
		<Container>
			<StickyStack>
				<Section border={false}>
					<Box display="flex" justifyContent="space-between">
						<Box display="flex" flexDirection="column">
							<Typography variant="body2" color={theme.palette.text.secondary}>
								{t('emissionForm.results.total', {
									context: translationContext,
								})}
							</Typography>

							<Box display="flex">
								<Typography variant="h1" component="p">
									{totalEmissions
										? getFormattedTotalWithUnit(totalEmissions)
										: '-'}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Section>

				{areCategoriesShown && (
					<HighlightedContainer mb={2}>
						<Section border={false}>
							{categories.map(category => (
								<Box key={category}>
									<Box
										display="flex"
										justifyContent="space-between"
										alignItems="center"
										pt={1.5}
									>
										<Box width="50%">
											<Typography
												variant="body2"
												color={theme.palette.text.secondary}
											>
												{t('emissionForm.categories.name', {
													context: category,
												})}
											</Typography>
										</Box>

										<Box width="45%" textAlign="end">
											<Typography variant="body2">
												{getFormattedTotalWithUnit(
													calculateCategoryTotal(
														emissionsValuesBreakdown?.[category]
													)
												)}
											</Typography>
										</Box>
									</Box>
									<Stack>
										{areSubcategoriesShown &&
											Object.entries(
												calculateSubcategoryTotals(
													emissionsValuesBreakdown?.[category]
												)
											).map(([name, value]) => (
												<Typography
													variant="body2"
													color={theme.palette.text.secondary}
													paddingLeft={1}
													paddingTop={0.5}
													key={`${category}-${name}`}
												>
													{`${t('emissions.stage.name', { context: name })}:
															${getFormattedTotalWithUnit(value)}
													`}
												</Typography>
											))}
									</Stack>
								</Box>
							))}
						</Section>
					</HighlightedContainer>
				)}

				<Box display="flex" alignItems="center" pb={2}>
					<VerifiedUserOutlinedIcon color="primary" />
					<Typography
						variant="body2"
						pl={2}
						color={theme.palette.text.secondary}
					>
						{viewType === 'supplier'
							? t('emissionForm.results.dataWillBeShared')
							: t('emissionForm.results.dataSharedCustomer')}
					</Typography>
				</Box>
			</StickyStack>
		</Container>
	)
}

export { TotalSection }
